// Banner de CONSENTIMIENTO DE COOKIES con consentimiento PREVIO (ePrivacy/GDPR).
//
// Aparece mientras el visitante no haya tomado una decisión. Ofrece aceptar
// todo, solo lo necesario, o abrir un panel de preferencias con toggles por
// categoría. La analítica de terceros (GA4/Contentsquare) no carga hasta que
// se concede la categoría `analytics` (ver analytics.ts + cookieConsent.ts).
//
// Accesibilidad (WCAG 2.2 AA):
//   - El banner es un `role="region"` con `aria-label`; no roba el foco pero es
//     alcanzable por teclado y anunciado.
//   - El panel de preferencias es un `role="dialog"` modal: atrapa el foco,
//     cierra con Escape, devuelve el foco al disparador y usa `aria-modal`.
//   - Cada toggle es un checkbox con `id` + `<label htmlFor>` explícito y texto
//     descriptivo asociado por `aria-describedby`.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../ui/components.js';
import { recordConsentPreferences } from '../marketing/publicApi.js';
import {
  acceptAll,
  getConsent,
  hasDecided,
  rejectAll,
  setConsent,
  onConsentChange,
  COOKIE_CONSENT_VERSION,
  type CookieConsent,
} from './cookieConsent.js';

/**
 * Persiste en el backend (prueba append-only) la decisión de consentimiento.
 * Best-effort: un fallo de red no debe romper la UI (la decisión ya rige
 * localmente y gobierna la carga de scripts). Se llama tras cada decisión.
 */
function persistConsent(consent: CookieConsent, locale: string): void {
  void recordConsentPreferences(
    [
      { purpose: 'analytics', granted: consent.analytics },
      { purpose: 'session_replay', granted: consent.replay },
      { purpose: 'marketing', granted: consent.marketing },
    ],
    { policyVersion: COOKIE_CONSENT_VERSION, locale },
  ).catch(() => {
    // Silencioso: el consentimiento local ya se aplicó; el servidor se
    // reconciliará en la siguiente oportunidad (p. ej. desde la cuenta).
  });
}

/** Un toggle de categoría de consentimiento (checkbox accesible). */
function CategoryToggle({
  id,
  checked,
  disabled,
  onChange,
  title,
  description,
}: {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (next: boolean) => void;
  title: string;
  description: string;
}) {
  const descId = `${id}-desc`;
  return (
    <div className="flex items-start gap-space-sm p-space-sm rounded-lg bg-surface-deck border border-border-subtle">
      <input
        type="checkbox"
        id={id}
        checked={checked}
        disabled={disabled}
        aria-describedby={descId}
        onChange={(e) => onChange?.(e.target.checked)}
        className="mt-1 accent-primary w-4 h-4 shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-60"
      />
      <label htmlFor={id} className="flex flex-col gap-0.5 cursor-pointer">
        <span className="font-body-md text-body-md text-on-surface font-semibold">{title}</span>
        <span id={descId} className="font-body-sm text-body-sm text-on-surface-variant leading-relaxed">
          {description}
        </span>
      </label>
    </div>
  );
}

/** Panel modal de preferencias detalladas por categoría. */
function PreferencesDialog({
  onClose,
  onSaved,
  returnFocusTo,
}: {
  onClose: () => void;
  onSaved: () => void;
  returnFocusTo: HTMLElement | null;
}) {
  const { t, i18n } = useTranslation();
  const current = getConsent();
  const [analytics, setAnalytics] = useState(current?.analytics ?? false);
  const [replay, setReplay] = useState(current?.replay ?? false);
  const [marketing, setMarketing] = useState(current?.marketing ?? false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFocusRef = useRef<HTMLButtonElement>(null);

  // Al montar: foco al primer control; al desmontar: devolver el foco.
  useEffect(() => {
    firstFocusRef.current?.focus();
    return () => {
      returnFocusTo?.focus();
    };
  }, [returnFocusTo]);

  // Cierre con Escape + atrapado de foco (Tab cíclico dentro del diálogo).
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  function save() {
    const next = setConsent({ analytics, replay: analytics ? replay : false, marketing });
    persistConsent(next, i18n.language);
    onSaved();
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 p-space-sm"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-prefs-title"
        aria-describedby="cookie-prefs-intro"
        onKeyDown={onKeyDown}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-surface-base border border-border-subtle rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col gap-space-md p-space-lg max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-space-sm">
          <h2 id="cookie-prefs-title" className="font-headline-sm text-headline-sm text-on-surface font-bold">
            {t('consent.prefs.title')}
          </h2>
          <button
            ref={firstFocusRef}
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="shrink-0 p-1 rounded-lg text-on-surface-variant hover:text-on-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <Icon name="close" className="text-[22px]" />
          </button>
        </div>

        <p id="cookie-prefs-intro" className="font-body-sm text-body-sm text-on-surface-variant leading-relaxed">
          {t('consent.prefs.intro')}
        </p>

        <div className="flex flex-col gap-space-sm">
          <CategoryToggle
            id="consent-necessary"
            checked
            disabled
            title={t('consent.prefs.necessaryTitle')}
            description={t('consent.prefs.necessaryDesc')}
          />
          <CategoryToggle
            id="consent-analytics"
            checked={analytics}
            onChange={(v) => {
              setAnalytics(v);
              if (!v) setReplay(false);
            }}
            title={t('consent.prefs.analyticsTitle')}
            description={t('consent.prefs.analyticsDesc')}
          />
          <CategoryToggle
            id="consent-replay"
            checked={replay}
            disabled={!analytics}
            onChange={setReplay}
            title={t('consent.prefs.replayTitle')}
            description={t('consent.prefs.replayDesc')}
          />
          <CategoryToggle
            id="consent-marketing"
            checked={marketing}
            onChange={setMarketing}
            title={t('consent.prefs.marketingTitle')}
            description={t('consent.prefs.marketingDesc')}
          />
        </div>

        <p className="font-body-sm text-body-sm text-on-surface-variant">
          {t('consent.prefs.moreInfo')}{' '}
          <Link to="/legal/privacidad" className="text-primary hover:underline">
            {t('consent.privacyLink')}
          </Link>
        </p>

        <div className="flex flex-col sm:flex-row gap-space-sm">
          <button
            type="button"
            onClick={() => {
              persistConsent(rejectAll(), i18n.language);
              onSaved();
            }}
            className="btn-ghost border border-border-subtle flex-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            {t('consent.rejectAll')}
          </button>
          <button
            type="button"
            onClick={save}
            className="btn-cyan flex-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <Icon name="check_circle" className="text-[18px]" />
            {t('consent.prefs.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Banner de consentimiento de cookies. Se auto-oculta cuando hay una decisión.
 * Puede forzarse su apertura desde fuera escuchando el evento global
 * `saands:open-cookie-preferences` (ver `openCookiePreferences`).
 */
export function CookieBanner() {
  const { t, i18n } = useTranslation();
  const [decided, setDecided] = useState(hasDecided());
  const [showPrefs, setShowPrefs] = useState(false);
  // Disparador que abrió el panel, para devolverle el foco al cerrar.
  const triggerRef = useRef<HTMLElement | null>(null);

  // Si el consentimiento cambia por otra vía, refrescamos la visibilidad.
  useEffect(() => onConsentChange((c: CookieConsent | null) => setDecided(c !== null)), []);

  // Permite reabrir las preferencias desde el footer / cuenta (evento global).
  useEffect(() => {
    function reopen() {
      triggerRef.current = (document.activeElement as HTMLElement) ?? null;
      setShowPrefs(true);
    }
    window.addEventListener('saands:open-cookie-preferences', reopen);
    return () => window.removeEventListener('saands:open-cookie-preferences', reopen);
  }, []);

  const closePrefs = useCallback(() => setShowPrefs(false), []);
  const afterSave = useCallback(() => {
    setShowPrefs(false);
    setDecided(true);
  }, []);

  // Nada que mostrar: ni banner (ya decidió) ni panel abierto.
  if (decided && !showPrefs) return null;

  return (
    <>
      {!decided && (
        <section
          role="region"
          aria-label={t('consent.banner.ariaLabel')}
          className="fixed inset-x-0 bottom-0 z-50 bg-surface-base border-t border-border-subtle shadow-[0_-4px_24px_rgba(0,0,0,0.3)] pb-safe"
        >
          <div className="max-w-5xl mx-auto px-margin-mobile py-space-md flex flex-col md:flex-row md:items-center gap-space-md">
            <div className="flex items-start gap-space-sm flex-1">
              <Icon name="cookie" className="text-primary text-[24px] shrink-0 mt-0.5" />
              <p className="font-body-sm text-body-sm text-on-surface-variant leading-relaxed">
                {t('consent.banner.message')}{' '}
                <Link to="/legal/privacidad" className="text-primary hover:underline">
                  {t('consent.privacyLink')}
                </Link>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-space-xs shrink-0">
              <button
                type="button"
                onClick={(e) => {
                  triggerRef.current = e.currentTarget;
                  setShowPrefs(true);
                }}
                className="btn-ghost border border-border-subtle focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                {t('consent.banner.preferences')}
              </button>
              <button
                type="button"
                onClick={() => {
                  persistConsent(rejectAll(), i18n.language);
                  setDecided(true);
                }}
                className="btn-ghost border border-border-subtle focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                {t('consent.rejectAll')}
              </button>
              <button
                type="button"
                onClick={() => {
                  persistConsent(acceptAll(), i18n.language);
                  setDecided(true);
                }}
                className="btn-cyan focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                {t('consent.acceptAll')}
              </button>
            </div>
          </div>
        </section>
      )}

      {showPrefs && (
        <PreferencesDialog onClose={closePrefs} onSaved={afterSave} returnFocusTo={triggerRef.current} />
      )}
    </>
  );
}

/**
 * Abre el panel de preferencias de cookies desde cualquier parte (footer,
 * pantalla de cuenta). Usa un evento global que el `CookieBanner` escucha.
 */
export function openCookiePreferences(): void {
  window.dispatchEvent(new CustomEvent('saands:open-cookie-preferences'));
}
